import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import sendError from "../../utils/sendError";
import { ERROR_CODE_400 } from "manifest-bridge-core/src/errors";
import { useContext, useEffect } from "react";
import { AppContext } from "../../context/AppContext";
import { TransferContext } from "../../context/TransferContext";
import { BRIDGE_DEMO, BRIDGE_DEVELOPMENT, BRIDGE_PRODUCTION, BridgeEnv, ErrorCode } from "../../typings";
import { sendAppVersionToSentry } from "../../lib/sentry";

const useLaunchController = () => {
  const { launchId, clientId } = useParams();
  const [searchParams] = useSearchParams();
  const { claimToken, setIsWebview } = useContext(AppContext);
  const { initBridge } = useContext(TransferContext);
  const navigate = useNavigate();

  if (!launchId) {
    const origin: string = searchParams.get("origin") || "*";
    sendError(
      {
        errorCode: ERROR_CODE_400,
        errorMessage: "Please pass launch token to launch bridge",
      },
      searchParams.get("webview") === "true",
      origin,
    );
    return;
  }

  useEffect(() => {
    const init = async () => {
      sendAppVersionToSentry();
      const isBridgeDemo = clientId === "bridge";

      const env: BridgeEnv =
        searchParams.get("environment")?.toLowerCase() === "production"
          ? BRIDGE_PRODUCTION
          : isBridgeDemo
            ? BRIDGE_DEMO
            : BRIDGE_DEVELOPMENT || BRIDGE_DEVELOPMENT;
      const origin: string = searchParams.get("origin") || "*";
      if (!launchId || !clientId) return;
      let $transferIntentId, $accessToken;
      let isInit: boolean | { errorCode: ErrorCode; errorMessage: string } = false;
      if (!isBridgeDemo) {
        const res = await claimToken({
          token: launchId,
          env,
          clientId,
          origin,
        });
        $transferIntentId = res.$transferIntentId;
        $accessToken = res.$accessToken;
        if (!$transferIntentId && !$accessToken && !clientId) return;
      } else {
        $transferIntentId = launchId;
        $accessToken = "bridge-token";
      }

      isInit = await initBridge({
        $transferIntentId,
        $accessToken,
        $clientId: clientId,
        env,
      });
      if (typeof isInit === "object") {
        sendError(isInit, searchParams.get("webview") === "true", origin);
        return;
      }
      if (isInit === true) {
        setIsWebview(searchParams.get("webview") === "true");
        Sentry.setTag("webview", searchParams.get("webview") || "false");
        Sentry.setTag("clientEnv", searchParams.get("environment") || "development");
        navigate("/transfer");
      }
    };

    init();
  }, []);

  return {
    launchId,
  };
};

export default useLaunchController;
